import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, Slide, SlideProps } from "@mui/material";
import { Button as ButtonAntd, message, Space } from "antd";
import {
  CloseCircleOutlined,
  VideoCameraOutlined,
  PauseCircleFilled,
  PlayCircleFilled,
  SaveFilled,
  ReloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  BulbTwoTone,
  InteractionTwoTone,
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { saveImageBlob } from "../../../helpers";
import { useRouterStore } from "../../../../libs/stores";

// -----------------------------------------
// Slide Transition (MUI) para el Dialog
// -----------------------------------------
const Transition = React.forwardRef<HTMLDivElement, SlideProps>(
  function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
  }
);

// -----------------------------------------
// Estilos in-line optimizados
// -----------------------------------------
const styles = {
  container: {
    position: "fixed",
    width: "100%",
    zIndex: 1,
    background: "none",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  video: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    margin: 0,
    padding: 0,
  },
  closeBtn: {
    position: "absolute",
    top: "10px",
    right: "10px",
    zIndex: 1601,
  },
  torchBtn: {
    position: "absolute",
    top: "10px",
    left: "10px",
    zIndex: 1601,
  },
  flipCameraBtn: {
    position: "absolute",
    bottom: "10px",
    left: "10px",
    zIndex: 1601,
  },
  controlButtons: {
    position: "absolute",
    bottom: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 1601,
  },
  saveBtn: {
    position: "absolute",
    bottom: "20px",
    right: "50%",
    transform: "translateX(50%)",
    width: "60px",
    height: "60px",
    zIndex: 1601,
  },
  clearBtn: {
    position: "absolute",
    bottom: "20px",
    right: "calc(50% - 80px)",
    transform: "translateX(50%)",
    width: "60px",
    height: "60px",
    zIndex: 1601,
  },
  counter: {
    position: "absolute",
    top: "10px",
    left: "60px",
    zIndex: 1601,
    backgroundColor: "rgba(0,0,0,0.6)",
    color: "#fff",
    padding: "5px 10px",
    borderRadius: "5px",
    fontSize: "16px",
  },
  zoomContainer: {
    position: "absolute",
    top: "150px",
    right: "10px",
    zIndex: 1601,
  },
} as any

interface VideoRecorderProps {
  open: boolean;
  handleClose: () => void;
  onVideoSaved: (videoKey: string) => void;
}

export const VideoRecorderNative: React.FC<VideoRecorderProps> = ({
  open,
  handleClose,
  onVideoSaved,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const photoWidth = useRouterStore((state) => state.photoWidth);
  const photoHeight = useRouterStore((state) => state.photoHeight);

  const [capturing, setCapturing] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [processing, setProcessing] = useState(false);
  const [paused, setPaused] = useState(false);
  const [counter, setCounter] = useState(0);
  const [ready, setReady] = useState(false);
  const [numberOfCameras, setNumberOfCameras] = useState(0);
  const [mirror, setMirror] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const [isChangingCamera, setIsChangingCamera] = useState(false);
  const [torchSupported, setTorchSupported] = useState<boolean | null>(null);

  // Constraints de la cámara (memoizadas)
  const videoConstraints = useMemo(
    () => ({
      facingMode: "environment",
      width: { min: 640, ideal: photoWidth || 1280 },
      height: { min: 360, ideal: photoHeight || 720 },
    }),
    [photoWidth, photoHeight]
  );

  const [currentConstraints, setCurrentConstraints] =
    useState<MediaTrackConstraints>(videoConstraints);

  // Verificar permisos de cámara
  const checkCameraPermissions = useCallback(async () => {
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });
      if (permissionStatus.state === "denied") {
        message.error(
          "El permiso para la cámara fue denegado. Habilita el acceso en la configuración del navegador."
        );
        return false;
      }
      if (permissionStatus.state === "prompt") {
        message.info("Por favor, permite el acceso a la cámara cuando se solicite.");
      }
      return true;
    } catch (err) {
      console.error("Error al verificar permisos de cámara:", err);
      return false;
    }
  }, []);

  // Iniciar stream de la cámara
  const startCamera = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      message.error("API de cámara no soportada en este navegador");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: currentConstraints,
        audio: true,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      setReady(true);
    } catch (err: any) {
      message.error("No se pudo acceder a la cámara");
      console.error(err);
    }
  }, [currentConstraints]);

  // Detener stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setReady(false);
  }, []);

  // Efecto: Iniciar cámara cuando el modal se abre
  useEffect(() => {
    if (!open) {
      stopCamera();
      setCapturing(false);
      setPaused(false);
      setIsTorchOn(false);
      setIsChangingCamera(false);
      setRecordedChunks([]);
      setCounter(0);
      return;
    }

    let isActive = true;
    (async () => {
      const hasPermission = await checkCameraPermissions();
      if (!hasPermission) return;

      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        if (!isActive) return;
        const videoDevices = devices.filter((i) => i.kind === "videoinput");
        if (videoDevices.length === 0) {
          message.error("No se detectaron cámaras en este dispositivo.");
          return;
        }
        setNumberOfCameras(videoDevices.length);
        await startCamera();
      } catch (err: any) {
        message.error("No se pudo inicializar la cámara");
        console.error(err);
      }
    })();

    return () => {
      isActive = false;
      stopCamera();
    };
  }, [open, checkCameraPermissions, startCamera, stopCamera]);

  // Contador para grabación
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (capturing && !paused) {
      timer = setInterval(() => {
        setCounter((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [capturing, paused]);

  // Obtener track de video
  const getVideoTrack = useCallback((): MediaStreamTrack | null => {
    return streamRef.current?.getVideoTracks()[0] || null;
  }, []);

  // Manejar datos de grabación
  const handleDataAvailable = useCallback(({ data }: BlobEvent) => {
    if (data.size > 0) {
      setRecordedChunks((prev) => [...prev, data]);
    }
  }, []);

  // Iniciar grabación
  const handleStartCapture = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) {
      message.error("No se pudo obtener el stream de video");
      return;
    }
    try {
      const options = { mimeType: "video/webm;codecs=vp8,opus" };
      const recorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = recorder;
      recorder.addEventListener("dataavailable", handleDataAvailable);
      recorder.addEventListener("error", (event) => {
        console.error("Error en MediaRecorder:", event);
        message.error("Error durante la grabación");
        setCapturing(false);
      });
      recorder.start(1000); // Grabar en fragmentos de 1s para mejor manejo
      setCapturing(true);
      setRecordedChunks([]);
      setCounter(0);
      message.success("Grabación iniciada");
    } catch (error) {
      console.error("Error al iniciar grabación:", error);
      message.error("Error al iniciar grabación");
    }
  }, [handleDataAvailable]);

  // Detener grabación
  const handleStopCapture = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setCapturing(false);
      setPaused(false);
    }
  }, []);

  // Pausar grabación
  const handlePauseCapture = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.pause();
      setPaused(true);
    }
  }, []);

  // Reanudar grabación
  const handleResumeCapture = useCallback(() => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.resume();
      setPaused(false);
    }
  }, []);

  // Guardar video
  const handleSaveVideo = useCallback(async () => {
    setProcessing(true);
    try {
      if (recordedChunks.length > 0) {
        const blob = new Blob(recordedChunks, { type: "video/webm" });
        const videoKey = uuidv4();
        await saveImageBlob(videoKey, blob);
        onVideoSaved(videoKey);
        setRecordedChunks([]);
        handleClose();
      }
    } catch (error: any) {
      message.error(`Error al guardar el video: ${error.toString()}`);
    } finally {
      setProcessing(false);
    }
  }, [recordedChunks, onVideoSaved, handleClose]);

  // Limpiar grabación
  const handleClearRecording = useCallback(() => {
    setRecordedChunks([]);
    setCounter(0);
    message.info("Video grabado limpiado. Puedes volver a grabar.");
  }, []);

  // Cambiar facing mode
  const changeFacingMode = useCallback(() => {
    if (!ready) return;
    message.info("Cambiando modo de cámara...");
    setIsChangingCamera(true);

    setCurrentConstraints((prev) => {
      const newFacingMode = prev.facingMode === "user" ? "environment" : "user";
      setMirror(newFacingMode === "user");
      return { ...prev, facingMode: newFacingMode };
    });

    stopCamera();
    setTimeout(startCamera, 100);
    setTimeout(() => setIsChangingCamera(false), 200);
  }, [ready, startCamera, stopCamera]);

  // Control de zoom
  const handleZoomChange = useCallback(
    async (increment: number) => {
      const track = getVideoTrack();
      if (!track) {
        message.error("No hay track de video disponible");
        return;
      }
      const capabilities = track.getCapabilities?.() as any;
      if (!capabilities?.zoom) {
        message.error("Zoom no soportado en este dispositivo");
        return;
      }
      const settings = track.getSettings() as any;
      const newZoom = Math.min(
        capabilities.zoom.max,
        Math.max(capabilities.zoom.min, (settings.zoom || 0) + increment)
      );
      try {
        await track.applyConstraints({ advanced: [{ zoom: newZoom }] });
      } catch (err) {
        message.error("Error al aplicar zoom");
        console.error(err);
      }
    },
    [getVideoTrack]
  );

  const handleZoomIn = useCallback(() => handleZoomChange(0.5), [handleZoomChange]);
  const handleZoomOut = useCallback(() => handleZoomChange(-0.5), [handleZoomChange]);

  // Control de flash (torch) con cacheo
  const handleTorchToggle = useCallback(async () => {
    const track = getVideoTrack();
    if (!track) {
      message.error("No hay track de video disponible");
      return;
    }

    if (torchSupported === false) {
      message.info("El flash no es compatible con este dispositivo o navegador.");
      return;
    }

    const newTorchState = !isTorchOn;
    try {
      await track.applyConstraints({
        advanced: [{ torch: newTorchState }],
      });
      setIsTorchOn(newTorchState);
      setTorchSupported(true);
      message.success(newTorchState ? "Linterna encendida" : "Linterna apagada");
    } catch (err: any) {
      console.error("Error al activar/desactivar el flash:", err.name, err.message);
      setTorchSupported(false);
      message.info("El flash no es compatible con este dispositivo o navegador.");
    }
  }, [getVideoTrack, isTorchOn, torchSupported]);

  // Memoizar botones para evitar re-renderizados
  const renderButtons = useMemo(
    () => (
      <>
        {/* Botón cerrar */}
        <ButtonAntd
          style={styles.closeBtn}
          size="large"
          onClick={handleClose}
          type="primary"
          danger
          shape="circle"
          icon={<CloseCircleOutlined />}
        />

        {/* Contador */}
        {capturing && (
          <div style={styles.counter}>{counter} s</div>
        )}

        {/* Botones de control de grabación */}
        <Space style={styles.controlButtons}>
          {capturing ? (
            <>
              {paused ? (
                <ButtonAntd
                  size="large"
                  onClick={handleResumeCapture}
                  type="primary"
                  shape="circle"
                  icon={<PlayCircleFilled />}
                />
              ) : (
                <ButtonAntd
                  size="large"
                  onClick={handlePauseCapture}
                  type="primary"
                  shape="circle"
                  icon={<PauseCircleFilled />}
                />
              )}
              <ButtonAntd
                size="large"
                onClick={handleStopCapture}
                type="primary"
                danger
                shape="circle"
                icon="⬜"
              />
            </>
          ) : (
            <ButtonAntd
              size="large"
              onClick={handleStartCapture}
              type="primary"
              shape="circle"
              icon={<VideoCameraOutlined />}
            />
          )}
        </Space>

        {/* Botones de guardar/limpiar */}
        {!capturing && recordedChunks.length > 0 && (
          <>
            <ButtonAntd
              style={styles.saveBtn}
              size="large"
              onClick={handleSaveVideo}
              type="primary"
              shape="circle"
              icon={<SaveFilled />}
              loading={processing}
            />
            <ButtonAntd
              style={styles.clearBtn}
              size="large"
              onClick={handleClearRecording}
              type="default"
              shape="circle"
              icon={<ReloadOutlined />}
            />
          </>
        )}

        {/* Cambiar facing mode */}
        <ButtonAntd
          style={styles.flipCameraBtn}
          size="large"
          disabled={numberOfCameras < 2 || !ready}
          onClick={changeFacingMode}
          type="dashed"
          shape="circle"
          icon={
            <InteractionTwoTone
              twoToneColor={numberOfCameras < 2 ? "#FFD700" : "#FF0000"}
            />
          }
        />

        {/* Encender/apagar torch */}
        <ButtonAntd
          style={styles.torchBtn}
          size="large"
          onClick={handleTorchToggle}
          type="dashed"
          shape="circle"
          disabled={torchSupported === false}
          icon={<BulbTwoTone twoToneColor={isTorchOn ? "#FFD700" : "#FF0000"} />}
        />

        {/* Controles de zoom */}
        <div style={styles.zoomContainer}>
          <Space direction="vertical">
            <ButtonAntd
              size="large"
              onClick={handleZoomIn}
              type="primary"
              shape="circle"
              icon={<ZoomInOutlined />}
              disabled={!ready}
            />
            <ButtonAntd
              size="large"
              onClick={handleZoomOut}
              type="primary"
              shape="circle"
              icon={<ZoomOutOutlined />}
              disabled={!ready}
            />
          </Space>
        </div>
      </>
    ),
    [
      handleClose,
      capturing,
      counter,
      paused,
      handleResumeCapture,
      handlePauseCapture,
      handleStopCapture,
      handleStartCapture,
      recordedChunks.length,
      handleSaveVideo,
      processing,
      handleClearRecording,
      numberOfCameras,
      ready,
      changeFacingMode,
      handleTorchToggle,
      torchSupported,
      isTorchOn,
      handleZoomIn,
      handleZoomOut,
    ]
  );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      keepMounted={false}
      sx={{
        margin: 0,
        padding: 0,
        "& .MuiDialog-paper": {
          margin: 0,
          padding: 0,
          maxHeight: "100vh",
          background: "rgba(39, 16, 128, 0.2)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
        },
      }}
    >
      <DialogContent
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          margin: 0,
          padding: 0,
          width: "100%",
          height: "100%",
          overflow: "hidden",
          background: "none",
        }}
      >
        <div style={styles.container}>
          {!isChangingCamera && (
            <video
              ref={videoRef}
              style={{
                ...styles.video,
                transform: mirror ? "scaleX(-1)" : "scaleX(1)",
              }}
              autoPlay
              playsInline
              muted
            />
          )}
        </div>
        {renderButtons}
      </DialogContent>
    </Dialog>
  );
};